import { useTranslation } from 'react-i18next';
import { FieldArray, Form, Formik } from 'formik';
import { Stack } from '@koob/margaret';
import { useAsync } from 'react-async';
import {
  modifyCurrencyExchangeRates,
  getCurrencyExchangeRates,
} from 'api/node/currencyExchangeRates';
import {
  KoobTable,
  KoobTableHeader,
  KoobTableBody,
  KoobTableRow,
  KoobTableHead,
  KoobTableCell,
  KoobButton,
} from '@koob/ui';
import React from 'react';
import { FastTextField, TogglableField } from '../../../../components/Fields';
import { formatCurrencyLabel } from '../../../../utils';
import { formatDateTime } from '@koob/dates';
import FastCurrencyField from '../../../../components/Fields/FastCurrencyField';
import { useSnack } from '../../../../hooks';
import * as Yup from 'yup';
import { getCurrencies } from 'api/node/currencies';

const ExchangeRatesForm = ({
  dmcCurrency,
  isAllowedToManage,
  organizationId,
}) => {
  const { t } = useTranslation('currencyExchangeRateModal');
  const { notify } = useSnack();

  const { data, reload } = useAsync({
    promiseFn: getCurrencyExchangeRates,
    filters: { organizationId },
  });
  const exchangeRates = data?.data ?? [];

  const { data: currenciesData } = useAsync({
    promiseFn: getCurrencies,
  });

  const currenciesOptions = (currenciesData?.data || []).map(
    ({ name: currency }) => ({
      value: currency,
      label: formatCurrencyLabel(currency),
    }),
  );

  const sourceCurrenciesOptions = (currenciesData?.data || []).map(
    ({ name }) => ({
      value: name,
      label: formatCurrencyLabel(name),
    }),
  );

  const initialValues = {
    rates: exchangeRates,
  };

  const disableExchangeRate = async (applyChange) => {
    if (!window.confirm(t('form.confirmArchivingCurrencyRate'))) {
      return;
    }
    applyChange();
  };

  const handleCurrencyFormSubmit = async (values, { resetForm }) => {
    try {
      await modifyCurrencyExchangeRates({ organizationId }, values.rates);
      await resetForm();
      reload();
      notify(t('form.currenciesModifiedSuccessfully'));
    } catch (e) {
      notify(t('form.existingExchangeRate'), { type: 'error' });
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleCurrencyFormSubmit}
      enableReinitialize={true}
      validationSchema={Yup.object().shape({
        rates: Yup.array()
          .of(
            Yup.object().shape({
              fromCurrency: Yup.string().required(t('errors:required')),
              toCurrency: Yup.string().required(t('errors:required')),
              rate: Yup.number().required(t('errors:required')),
            }),
          )
          .min(1),
      })}
    >
      {({ values, isLoading, dirty, isValid, setFieldValue }) => (
        <Stack direction="row" gutterSize={1} size="full">
          <Form style={{ width: '100%' }}>
            <FieldArray
              name={`rates`}
              render={({ push, remove }) => {
                return (
                  <>
                    <div className="mb-3">
                      <KoobButton
                        variant="secondary"
                        onClick={() => {
                          push({ fromCurrency: undefined, archived: false });
                        }}
                        disabled={!isAllowedToManage}
                      >
                        <i className="fa-light fa-circle-plus" />
                        {t('form.newExchangeRate')}
                      </KoobButton>
                    </div>

                    <Stack
                      direction={'column'}
                      size="full"
                      alignX="center"
                      gutterSize={1}
                    >
                      <KoobTable>
                        <KoobTableHeader>
                          <KoobTableRow>
                            <KoobTableHead>
                              {t('form.myCurrency')}
                            </KoobTableHead>
                            <KoobTableHead>
                              {t('form.partnersCurrency')}
                            </KoobTableHead>
                            <KoobTableHead>{t('form.rate')}</KoobTableHead>
                            <KoobTableHead>{t('form.createdAt')}</KoobTableHead>
                            <KoobTableHead>
                              {t('form.archivedAt')}
                            </KoobTableHead>
                            <KoobTableHead>{t('form.actions')}</KoobTableHead>
                          </KoobTableRow>
                        </KoobTableHeader>

                        <KoobTableBody>
                          {values.rates?.map((rate, idx) => (
                            <KoobTableRow key={idx}>
                              <KoobTableCell>
                                <TogglableField
                                  disabled={
                                    Boolean(rate?.id) || !isAllowedToManage
                                  }
                                  name={`rates.${idx}.fromCurrency`}
                                  activeElement={
                                    <Stack
                                      direction="column"
                                      size="full"
                                      gutterSize={0.5}
                                    >
                                      <FastCurrencyField
                                        currenciesOptions={sourceCurrenciesOptions.filter(
                                          (option) =>
                                            option.value !==
                                            values.rates[idx].toCurrency,
                                        )}
                                        name={`rates.${idx}.fromCurrency`}
                                      />
                                    </Stack>
                                  }
                                  idleElement={
                                    <Stack
                                      direction="column"
                                      gutterSize={0.5}
                                      size="full"
                                    >
                                      <Stack gutterSize={0.25} size="full">
                                        <span>
                                          {rate?.fromCurrency ?? 'N/A'}
                                        </span>
                                      </Stack>
                                    </Stack>
                                  }
                                />
                              </KoobTableCell>

                              <KoobTableCell>
                                <TogglableField
                                  disabled={!isAllowedToManage}
                                  name={`rates.${idx}.toCurrency`}
                                  activeElement={
                                    <Stack
                                      direction="column"
                                      size="full"
                                      gutterSize={0.5}
                                    >
                                      <FastCurrencyField
                                        currenciesOptions={currenciesOptions.filter(
                                          (option) =>
                                            option.value !==
                                            values.rates[idx].fromCurrency,
                                        )}
                                        name={`rates.${idx}.toCurrency`}
                                      />
                                    </Stack>
                                  }
                                  idleElement={
                                    <Stack
                                      direction="column"
                                      gutterSize={0.5}
                                      size="full"
                                    >
                                      <Stack gutterSize={0.25} size="full">
                                        <span>{rate?.toCurrency ?? 'N/A'}</span>
                                      </Stack>
                                    </Stack>
                                  }
                                />
                              </KoobTableCell>

                              <KoobTableCell>
                                <TogglableField
                                  disabled={
                                    Boolean(rate?.id) || !isAllowedToManage
                                  }
                                  name={`rates.${idx}.rate`}
                                  activeElement={
                                    <Stack
                                      direction="column"
                                      size="full"
                                      gutterSize={0.5}
                                    >
                                      <FastTextField
                                        name={`rates.${idx}.rate`}
                                        disabled={Boolean(rate?.id)}
                                        type={'number'}
                                        min="0"
                                        step={0.01}
                                      />
                                    </Stack>
                                  }
                                  idleElement={
                                    <Stack
                                      direction="column"
                                      gutterSize={0.5}
                                      size="full"
                                    >
                                      <Stack gutterSize={0.25} size="full">
                                        <span>
                                          {rate?.rate && rate?.rate !== ''
                                            ? rate?.rate
                                            : 'N/A'}
                                        </span>
                                      </Stack>
                                    </Stack>
                                  }
                                />
                              </KoobTableCell>

                              <KoobTableCell>
                                <TogglableField
                                  disabled={true}
                                  name={`rates.${idx}.createdAt`}
                                  activeElement={
                                    <Stack
                                      direction="column"
                                      size="full"
                                      gutterSize={0.5}
                                    >
                                      <FastTextField
                                        name={`rates.${idx}.createdAt`}
                                        disabled={true}
                                      />
                                    </Stack>
                                  }
                                  idleElement={
                                    <Stack
                                      direction="column"
                                      gutterSize={0.5}
                                      size="full"
                                    >
                                      <Stack gutterSize={0.25} size="full">
                                        <span>
                                          {rate?.createdAt
                                            ? formatDateTime(
                                                rate?.createdAt,
                                                'DD/MM/YYYY',
                                              )
                                            : ''}
                                        </span>
                                      </Stack>
                                    </Stack>
                                  }
                                />
                              </KoobTableCell>

                              <KoobTableCell>
                                <TogglableField
                                  disabled={true}
                                  name={`rates.${idx}.discardedAt`}
                                  activeElement={
                                    <Stack
                                      direction="column"
                                      size="full"
                                      gutterSize={0.5}
                                    >
                                      <FastTextField
                                        name={`rates.${idx}.discardedAt`}
                                        disabled={true}
                                      />
                                    </Stack>
                                  }
                                  idleElement={
                                    <Stack
                                      direction="column"
                                      gutterSize={0.5}
                                      size="full"
                                    >
                                      <Stack gutterSize={0.25} size="full">
                                        <span>
                                          {rate?.discardedAt
                                            ? formatDateTime(
                                                rate?.discardedAt,
                                                'DD/MM/YYYY',
                                              )
                                            : ''}
                                        </span>
                                      </Stack>
                                    </Stack>
                                  }
                                />
                              </KoobTableCell>

                              <KoobTableCell>
                                {rate?.id ? (
                                  <KoobButton
                                    variant="secondary"
                                    disabled={
                                      Boolean(rate?.archived) ||
                                      !isAllowedToManage
                                    }
                                    onClick={async (e) => {
                                      e.preventDefault();
                                      disableExchangeRate(() => {
                                        setFieldValue(
                                          `rates.${idx}.archived`,
                                          true,
                                        );
                                      });
                                    }}
                                  >
                                    <i className="fa-regular fa-box-archive" />
                                  </KoobButton>
                                ) : (
                                  <KoobButton
                                    variant="secondary"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      remove(idx);
                                    }}
                                    disabled={!isAllowedToManage}
                                  >
                                    <i className="fa-regular fa-circle-minus" />
                                  </KoobButton>
                                )}
                              </KoobTableCell>
                            </KoobTableRow>
                          ))}
                        </KoobTableBody>
                      </KoobTable>
                    </Stack>
                  </>
                );
              }}
            />

            <Stack marginTop={2} size="full" alignX="flex-end">
              <KoobButton
                type="submit"
                variant="primary"
                style={{ minWidth: 120, minHeight: 40, marginLeft: 25 }}
                disabled={isLoading || !dirty || !isValid}
              >
                {t('misc:save')}
              </KoobButton>
            </Stack>
          </Form>
        </Stack>
      )}
    </Formik>
  );
};

export default ExchangeRatesForm;
